import React, { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Resource } from '../../../types';
import { useTranslation } from '../../../contexts/TranslationContext';

interface SortingItem {
  emoji: string;
  name: string;
  category: string;
}

interface SortingCategory {
  id: string;
  name: string;
  color: string;
  emoji: string;
}

interface SortingGameConfigurationFormProps {
  resource: Resource;
  onUpdate: (updates: Partial<Resource>) => void;
}

export const SortingGameConfigurationForm: React.FC<SortingGameConfigurationFormProps> = ({
  resource,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<SortingItem[]>(resource.items || [
    { emoji: '🐔', name: 'poule', category: 'animaux' },
    { emoji: '🐑', name: 'mouton', category: 'animaux' },
    { emoji: '🥄', name: 'pilon', category: 'objets' }
  ]);

  const [categories, setCategories] = useState<SortingCategory[]>(resource.categories || [
    { id: 'animaux', name: 'Animaux', color: '#EF4444', emoji: '🐾' },
    { id: 'objets', name: 'Objets', color: '#3B82F6', emoji: '📦' }
  ]);

  const updateResource = (field: string, value: any) => {
    onUpdate({ [field]: value });
  };

  const addItem = () => {
    const newItems = [...items, { emoji: '🎯', name: 'nouveau', category: categories[0]?.id || 'animaux' }];
    setItems(newItems);
    updateResource('items', newItems);
  };

  const removeItem = (index: number) => {
    const newItems = items.filter((_: SortingItem, i: number) => i !== index);
    setItems(newItems);
    updateResource('items', newItems);
  };

  const updateItem = (index: number, field: keyof SortingItem, value: string) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
    updateResource('items', newItems);
  };

  const addCategory = () => {
    const newCategory: SortingCategory = {
      id: `category_${Date.now()}`,
      name: 'Nouvelle catégorie',
      color: '#10B981',
      emoji: '📁'
    };
    const newCategories = [...categories, newCategory];
    setCategories(newCategories);
    updateResource('categories', newCategories);
  };

  const addColorCategories = () => {
    const colorCategories = [
      { id: 'rouge', name: 'Rouge', color: '#DC2626', emoji: '🔴' },
      { id: 'jaune', name: 'Jaune', color: '#F59E0B', emoji: '🟡' },
      { id: 'bleu', name: 'Bleu', color: '#2563EB', emoji: '🔵' },
      { id: 'blanc', name: 'Blanc', color: '#6B7280', emoji: '⚪' },
      { id: 'marron', name: 'Marron', color: '#92400E', emoji: '🟤' }
    ];
    
    // Add only categories that don't already exist
    const existingIds = categories.map(cat => cat.id);
    const newColorCategories = colorCategories.filter(cat => !existingIds.includes(cat.id));
    
    if (newColorCategories.length > 0) {
      const newCategories = [...categories, ...newColorCategories];
      setCategories(newCategories);
      updateResource('categories', newCategories);
    }
  };

  const addSizeCategories = () => {
    const sizeCategories = [
      { id: 'petit', name: 'Petit', color: '#10B981', emoji: '🔸' },
      { id: 'moyen', name: 'Moyen', color: '#F59E0B', emoji: '🔶' },
      { id: 'grand', name: 'Grand', color: '#EF4444', emoji: '🔷' }
    ];
    
    // Add only categories that don't already exist
    const existingIds = categories.map(cat => cat.id);
    const newSizeCategories = sizeCategories.filter(cat => !existingIds.includes(cat.id));
    
    if (newSizeCategories.length > 0) {
      const newCategories = [...categories, ...newSizeCategories];
      setCategories(newCategories);
      updateResource('categories', newCategories);
    }
  };

  const removeCategory = (index: number) => {
    if (categories.length <= 2) return; // Keep at least 2 categories
    const newCategories = categories.filter((_: SortingCategory, i: number) => i !== index);
    setCategories(newCategories);
    updateResource('categories', newCategories);
  };

  const updateCategory = (index: number, field: keyof SortingCategory, value: string) => {
    const newCategories = [...categories];
    newCategories[index][field] = value;
    setCategories(newCategories);
    updateResource('categories', newCategories);
  };

  const emojiSuggestions = [
    '🐔', '🐑', '🐦', '🥄', '💡', '🏠', '👒', '🍎', '🚗', '🌳',
    // Couleurs
    '🔴', '🟡', '🔵', '⚪', '🟤', '🟢', '🟣', '🟠', '⚫',
    // Tailles
    '🔸', '🔶', '🔷', '📏', '📐', '📊',

    '⭐', '❤️', '🌈', '🎈', '🎯', '🔔', '🔑', '📚', '✏️', '🎨'
  ];

  const categoryEmojiSuggestions = [
    // Nature
    '🐾', '📦', '🌳', '🍎', '🚗', '🏠', '👒', '🥄', '💡',
    // Couleurs
    '🔴', '🟡', '🔵', '⚪', '🟤', '🟢', '🟣', '🟠', '⚫',
    // Tailles
    '🔸', '🔶', '🔷', '📏', '📐', '📊',
    // Autres
    '⭐', '❤️', '🌈', '🎈', '🎯', '🔔', '🔑', '📚', '✏️', '🎨'
  ];

  const colorOptions = [
    { value: 'none', label: 'Aucun' },
    { value: '#EF4444', label: 'Rouge' },
    { value: '#3B82F6', label: 'Bleu' },
    { value: '#10B981', label: 'Vert' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#8B5CF6', label: 'Violet' },
    { value: '#EC4899', label: 'Rose' }
  ];

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameTitle')}
        </label>
        <input
          type="text"
          value={resource.title || t('resourceConfig.sortingGame')}
          onChange={(e) => updateResource('title', e.target.value)}
          className="input-field"
          placeholder={t('resourceConfig.enterGameTitle')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameDescription')}
        </label>
        <input
          type="text"
          value={resource.description || ''}
          onChange={(e) => updateResource('description', e.target.value)}
          className="input-field"
          placeholder={t('resourceConfig.enterGameDescription')}
        />
      </div>

      {/* Categories Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.categories')}
        </label>
        <div className="space-y-2">
          {categories.map((category, index) => (
            <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.categoryName')}
                </label>
                <input
                  type="text"
                  value={category.name}
                  onChange={(e) => updateCategory(index, 'name', e.target.value)}
                  className="input-field"
                  placeholder={t('resourceConfig.enterCategoryName')}
                />
              </div>
              
              <div className="w-20">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.emoji')}
                </label>
                <select
                  value={category.emoji}
                  onChange={(e) => updateCategory(index, 'emoji', e.target.value)}
                  className="input-field text-center"
                >
                  <option value="">Choisir...</option>
                  {categoryEmojiSuggestions.map(emoji => (
                    <option key={emoji} value={emoji}>{emoji}</option>
                  ))}
                </select>
              </div>
              
              <div className="w-24">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.color')}
                </label>
                <select
                  value={category.color}
                  onChange={(e) => updateCategory(index, 'color', e.target.value)}
                  className="input-field"
                >
                  {colorOptions.map(color => (
                    <option key={color.value} value={color.value}>{color.label}</option>
                  ))}
                </select>
              </div>
              
              {categories.length > 2 && (
                <button
                  type="button"
                  onClick={() => removeCategory(index)}
                  className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                  title={t('resourceConfig.removeCategory')}
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
        
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={addCategory}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            {t('resourceConfig.addCategory')}
          </button>
          
          <button
            type="button"
            onClick={addColorCategories}
            className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <Plus size={16} />
            Ajouter couleurs
          </button>
          
          <button
            type="button"
            onClick={addSizeCategories}
            className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus size={16} />
            Ajouter tailles
          </button>
        </div>
      </div>

      {/* Items Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameItems')}
        </label>
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex gap-2 items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-16">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.emoji')}
                </label>
                <select
                  value={item.emoji}
                  onChange={(e) => updateItem(index, 'emoji', e.target.value)}
                  className="input-field text-center"
                >
                  <option value="">Choisir...</option>
                  {emojiSuggestions.map(emoji => (
                    <option key={emoji} value={emoji}>{emoji}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.word')}
                </label>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(index, 'name', e.target.value)}
                  className="input-field"
                  placeholder={t('resourceConfig.enterWord')}
                />
              </div>
              
              <div className="flex-1">
                <label className="block text-xs text-gray-600 mb-1">
                  {t('resourceConfig.category')}
                </label>
                <select
                  value={item.category}
                  onChange={(e) => updateItem(index, 'category', e.target.value)}
                  className="input-field"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              
              <button
                type="button"
                onClick={() => removeItem(index)}
                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                title={t('resourceConfig.removeItem')}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        
        <button
          type="button"
          onClick={addItem}
          className="mt-2 flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} />
          {t('resourceConfig.addItem')}
        </button>
      </div>

      {/* Game Settings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('resourceConfig.gameSettings')}
        </label>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="autoStart"
              checked={resource.autoStart || false}
              onChange={(e) => updateResource('autoStart', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="autoStart" className="text-sm text-gray-600">
              {t('resourceConfig.autoStartGame')}
            </label>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showHints"
              checked={resource.showHints !== false}
              onChange={(e) => updateResource('showHints', e.target.checked)}
              className="rounded border-gray-300"
            />
            <label htmlFor="showHints" className="text-sm text-gray-600">
              {t('resourceConfig.showHints')}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};
